import * as React from 'react';
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';
import Skeleton from '@mui/material/Skeleton';
import { Editor } from '@tinymce/tinymce-react';
import { agreementHtmlAllowedTags } from '../../../shared/agreementHtml';
import { Translate } from '../../../shared/LanguageService';

type AgreementTextEditorProps = {
    label: string;
    value: string;
    onChange: (content: string) => void;
};

const attributesByTag: { [tag: string]: string[] } = {
    a: ['href', 'title'],
    img: ['src', 'alt', 'title', 'width', 'height'],
    td: ['colspan', 'rowspan'],
    th: ['colspan', 'rowspan']
};

const editorHeight = 400;
const toolbarHeight = 40;

function EditorSkeleton() {
    return (
        <Box
            role="progressbar"
            aria-label={Translate('customer-agreements/modal/editor-loading')}
            sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 1
            }}
        >
            <Skeleton variant="rectangular" height={toolbarHeight} />
            <Skeleton variant="rectangular" sx={{ flexGrow: 1 }} />
        </Box>
    );
}

const validElements = [
    '@[style]',
    ...agreementHtmlAllowedTags.map((tag) =>
        attributesByTag[tag] ? `${tag}[${attributesByTag[tag].join('|')}]` : tag
    )
].join(',');

export function AgreementTextEditor({ label, value, onChange }: AgreementTextEditorProps) {
    const [loaded, setLoaded] = React.useState(false);

    return (
        <Box sx={{ position: 'relative', minHeight: editorHeight }}>
            <GlobalStyles
                styles={(theme) => ({
                    '.tox.tox-tinymce-aux': { zIndex: theme.zIndex.modal + 1 }
                })}
            />
            {loaded ? null : <EditorSkeleton />}
            <Box sx={{ visibility: loaded ? 'visible' : 'hidden' }}>
                <Editor
                    value={value}
                    onEditorChange={(content: string) => onChange(content)}
                    onInit={() => setLoaded(true)}
                    init={{
                        height: editorHeight,
                        menubar: false,
                        branding: false,
                        plugins: 'lists link table code',
                        toolbar:
                            'undo redo | styleselect | bold italic underline | ' +
                            'alignleft aligncenter alignright alignjustify | bullist numlist | link table | code',
                        valid_elements: validElements,
                        valid_styles: { '*': 'text-align' },
                        link_assume_external_targets: 'https',
                        relative_urls: false,
                        remove_script_host: false,
                        iframe_aria_text: label
                    }}
                />
            </Box>
        </Box>
    );
}
